"use client";

import { useState } from "react";
import {
  Checkbox,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  type RobPlotId,
  RobResults,
} from "@/components/tools/research-discovery/rob-results";
import { useToolUi } from "@/components/tools/tool-ui";
import type {
  RobPaletteId,
  RobToolId,
} from "@/lib/tool-kit/research-discovery/rob-plot";

/** English strings. A translated content file overrides these through `ui`. */
export const RISK_OF_BIAS_PLOT_GENERATOR_STRINGS = {
  toolLabel: "Assessment tool",
  toolRob2: "RoB 2 (randomized trials)",
  toolRobinsi: "ROBINS-I (non-randomized studies)",
  plotLabel: "Plot",
  plotTraffic: "Traffic-light plot",
  plotSummary: "Summary bar plot",
  paletteLabel: "Colours",
  paletteCochrane: "Cochrane colours",
  paletteColourblind: "Colour-blind friendly",
  titleLabel: "Figure title (optional)",
  titlePlaceholder: "e.g. Risk of bias in the included trials",
  overallLabel: "Include the overall judgement",
  weightedLabel: "Weight the summary bars by study weight",
  weightedHint:
    "Needs a weight for every study, as the last column. Unweighted, every study counts once.",
  dataLabel: "Judgements — one study per line",
  dataHintRob2:
    "Study, D1, D2, D3, D4, D5, then an optional Overall judgement and an optional weight. Separate cells with commas, tabs or semicolons; pasting from a spreadsheet works. Judgements: Low, Some concerns, High, No information (or L, S, H, NI, or + − × ?).",
  dataHintRobinsi:
    "Study, D1 to D7, then an optional Overall judgement and an optional weight. Separate cells with commas, tabs or semicolons; pasting from a spreadsheet works. Judgements: Low, Moderate, Serious, Critical, No information (or L, M, S, C, NI).",
  exampleRob2:
    "Study, D1, D2, D3, D4, D5, Overall, Weight\nTrial 1, Low, Low, Low, Low, Some concerns, Some concerns, 21.4\nTrial 2, Low, Some concerns, Low, Low, Low, Some concerns, 18.2\nTrial 3, Some concerns, Some concerns, High, Low, Some concerns, High, 12.5\nTrial 4, Low, Low, Low, Some concerns, Low, Some concerns, 15.9\nTrial 5, High, Some concerns, Low, Some concerns, Some concerns, High, 8.3\nTrial 6, Low, Low, Low, Low, Low, Low, 23.7",
  exampleRobinsi:
    "Study, D1, D2, D3, D4, D5, D6, D7, Overall, Weight\nCohort 1, Moderate, Low, Low, Low, Low, Moderate, Low, Moderate, 30\nCohort 2, Serious, Moderate, Low, Low, Moderate, Moderate, Moderate, Serious, 25\nCohort 3, Critical, Moderate, Low, Moderate, Low, Low, Moderate, Critical, 10\nCohort 4, Moderate, Low, Moderate, Low, No information, Moderate, Low, Moderate, 20\nCohort 5, Serious, Serious, Low, Low, Moderate, Low, Low, Serious, 15",
  loadExample: "Load the example",
  clearData: "Clear",
  resultHeading: "Your figure",
  studiesRead: "one: {count} study read|other: {count} studies read",
  enterPrompt:
    "Enter at least one study to see the figure — or load the example to see how it works.",
  issueColumns:
    "Line {line}: not enough columns — a study needs its name and {expected} domain judgements.",
  issueJudgement:
    "Line {line}: “{value}” is not a judgement this tool uses, so the study was left out.",
  issueWeight:
    "Line {line}: “{value}” is not a usable weight (a positive number), so the study was left out.",
  moreIssues:
    "one: … and {count} more problem|other: … and {count} more problems",
  weightsMissing:
    "Weighting is switched on but not every study has a weight, so the bars count each study once.",
  overallDerived:
    "Studies without an Overall judgement get the worst of their domains. Enter an Overall column to use your own judgement.",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  copyData: "Copy data",
  copied: "Copied!",
  showTable: "Show the judgements as a table",
  studyColumn: "Study",
  rowColumn: "Domain",
  studyHeader: "Study",
  overallHeader: "Overall",
  overallName: "Overall risk of bias",
  keyHeading: "Domains",
  legendHeading: "Risk of bias",
  axisStudies: "Percentage of studies",
  axisWeight: "Percentage of weight",
  figureLabel: "Risk-of-bias {plot}",
  figureDescription:
    "{plot} for {count} studies assessed with {tool}, with {judgements}.",
  figureJudgements: "judgements of {levels}",
  trafficName: "traffic-light plot",
  summaryName: "summary bar plot",
  rob2Name: "RoB 2",
  robinsiName: "ROBINS-I",
  domainsRob2: [
    "Bias arising from the randomization process",
    "Bias due to deviations from intended interventions",
    "Bias due to missing outcome data",
    "Bias in measurement of the outcome",
    "Bias in selection of the reported result",
  ],
  domainsRobinsi: [
    "Bias due to confounding",
    "Bias in selection of participants into the study",
    "Bias in classification of interventions",
    "Bias due to deviations from intended interventions",
    "Bias due to missing data",
    "Bias in measurement of outcomes",
    "Bias in selection of the reported result",
  ],
  levelsRob2: {
    low: "Low risk",
    mid: "Some concerns",
    high: "High risk",
    critical: "Critical",
    ni: "No information",
    na: "Not applicable",
  },
  levelsRobinsi: {
    low: "Low risk",
    mid: "Moderate risk",
    high: "Serious risk",
    critical: "Critical risk",
    ni: "No information",
    na: "Not applicable",
  },
} as const;

export type RiskOfBiasPlotGeneratorStrings =
  typeof RISK_OF_BIAS_PLOT_GENERATOR_STRINGS;

export default function RiskOfBiasPlotGeneratorWidget() {
  const strings = useToolUi(RISK_OF_BIAS_PLOT_GENERATOR_STRINGS);
  const [tool, setTool] = useState<RobToolId>("rob2");
  const [plot, setPlot] = useState<RobPlotId>("traffic");
  const [palette, setPalette] = useState<RobPaletteId>("cochrane");
  const [title, setTitle] = useState("");
  const [overall, setOverall] = useState(true);
  const [weighted, setWeighted] = useState(false);
  const [texts, setTexts] = useState<Record<RobToolId, string>>({
    rob2: strings.exampleRob2,
    robinsi: strings.exampleRobinsi,
  });

  function setText(next: string) {
    setTexts((current) => ({ ...current, [tool]: next }));
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.toolLabel}
          name="robp-tool"
          onChange={setTool}
          options={[
            { value: "rob2", label: strings.toolRob2 },
            { value: "robinsi", label: strings.toolRobinsi },
          ]}
          value={tool}
        />
        <PillGroup
          label={strings.plotLabel}
          name="robp-plot"
          onChange={setPlot}
          options={[
            { value: "traffic", label: strings.plotTraffic },
            { value: "summary", label: strings.plotSummary },
          ]}
          value={plot}
        />
      </div>

      <TextAreaField
        hint={tool === "rob2" ? strings.dataHintRob2 : strings.dataHintRobinsi}
        id="robp-data"
        label={strings.dataLabel}
        onChange={setText}
        rows={9}
        value={texts[tool]}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setText(
              tool === "rob2" ? strings.exampleRob2 : strings.exampleRobinsi
            )
          }
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clearData}
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="robp-palette"
          label={strings.paletteLabel}
          onChange={setPalette}
          options={[
            { value: "cochrane", label: strings.paletteCochrane },
            { value: "colourblind", label: strings.paletteColourblind },
          ]}
          value={palette}
        />
        <TextField
          id="robp-title"
          label={strings.titleLabel}
          onChange={setTitle}
          placeholder={strings.titlePlaceholder}
          value={title}
        />
      </div>
      <div className="space-y-2">
        <Checkbox
          checked={overall}
          label={strings.overallLabel}
          onChange={setOverall}
        />
        {plot === "summary" ? (
          <div className="space-y-1">
            <Checkbox
              checked={weighted}
              label={strings.weightedLabel}
              onChange={setWeighted}
            />
            <p className={HINT}>{strings.weightedHint}</p>
          </div>
        ) : null}
      </div>

      <RobResults
        options={{ tool, plot, palette, title, overall, weighted }}
        strings={strings}
        text={texts[tool]}
      />
    </div>
  );
}
